import { TFunction } from "i18next";

/** Per-campaign engagement counts used to build the picker card's progress label. */
export interface ReachOutCampaignStatInput {
	/** Leads assigned to the campaign (the stats endpoint's `totalLeads`). */
	totalLeads: number;
	/** Assigned leads that have been contacted (≥1 sent message). */
	contacted: number;
}

/** A stat label split so the leading numeric token can be emphasised, mirroring the v33 mockup. */
export interface ReachOutCampaignStatLabel {
	/** Bold leading token — a fraction ("240/400") or count ("120"); empty when nothing to emphasise. */
	emphasis: string;
	/** Muted remainder of the label (e.g. " contacted (60%)"), or the full text when no emphasis. */
	label: string;
}

/**
 * Builds the per-campaign progress label shown on a reach-out picker card's second row. Mirrors the
 * v33 mockup's three states, using the campaign's own assigned-lead count as the denominator:
 * - no leads assigned (or no stats row)  → "No leads assigned yet"
 * - assigned, none contacted             → "{totalLeads} assigned"
 * - some contacted                       → "{contacted}/{totalLeads} contacted ({pct}%)"
 *
 * The percentage is contacted over assigned, rounded. The full string is built from i18n (so word
 * order stays translatable), then the leading numeric token is peeled off for bold emphasis — the
 * same treatment the mockup applies via its regex. A campaign with no assigned leads is absent from
 * the stats endpoint, so an `undefined` stat collapses to the "no leads assigned" state.
 */
export const formatReachOutCampaignStat = (
	stat: ReachOutCampaignStatInput | undefined,
	t: TFunction
): ReachOutCampaignStatLabel => {
	const totalLeads = stat?.totalLeads ?? 0;
	const contacted = stat?.contacted ?? 0;

	if (totalLeads <= 0) {
		return { emphasis: "", label: t("list.reachOut.picker.noLeadsAssigned") };
	}

	const full =
		contacted <= 0
			? t("list.reachOut.picker.assignedCount", { count: totalLeads })
			: t("list.reachOut.picker.contactedStat", {
					contacted,
					total: totalLeads,
					pct: Math.round((contacted / totalLeads) * 100),
				});

	// Peel off a leading digits/slash token ("240/400", "120") to bold; the rest stays muted.
	const match = full.match(/^([\d/]+)(.*)$/);

	return match ? { emphasis: match[1], label: match[2] } : { emphasis: "", label: full };
};
